import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Paper,
  LinearProgress,
  CircularProgress,
  RadioGroup,
  Radio,
  Autocomplete,
  Dialog,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Settings,
  Info,
  Label,
  Warning,
  Add,
  ChevronRight,
  Close,
  CloudUpload,
  Upload,
  CheckCircle,
  Error
} from '@mui/icons-material';
import FileUpload from '../common/FileUpload';
import { useNavigate, useParams } from 'react-router';
import ApiFetch from '../ApiFetch';
import { CheckIcon, CircleAlert } from 'lucide-react';
import Header from '../Header';
import Category from '../Category';
import Footer from '../Footer';

const MODEL_TYPES = [
  { value: 'standard', label: 'Standard' },
  { value: 'inpainting', label: 'Inpainting' },
  { value: 'refiner', label: 'Refiner' },
];

const STYLES = [
  { value: 'illustration', label: 'イラスト' },
  { value: 'semirealism', label: 'セミリアル' },
  { value: 'photorealistic', label: 'フォト' },
  { value: 'common', label: '汎用' },
  { value: 'other', label: 'その他' },
];

const BASE_MODELS = [
  { value: 'sd_1_5', label: 'Stable Diffusion 1.5' },
  { value: 'sdxl', label: 'Stable Diffusion XL' },
  { value: 'pony', label: 'Pony' },
  { value: 'illustrious', label: 'Illustrious' },
  { value: 'sd_2_1', label: 'Stable Diffusion 2.1' },
  { value: 'sd_3', label: 'Stable Diffusion 3' },
  { value: 'sd_3_5', label: 'Stable Diffusion 3.5' },
  { value: 'stable_cascade', label: 'Stable Cascade' },
  { value: 'flux_1', label: 'FLUX.1' },
  { value: 'framepack', label: 'FramePack' },
  { value: 'aivmx', label: 'AIVMX' },
  { value: 'aivm', label: 'AIVM' },
];

const CLIP_SKIP_OPTIONS = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
];

const CATEGORIES = [
  { value: 'image', label: '画像' },
  { value: 'video', label: '動画' },
  { value: 'audio', label: '音声' },
  { value: 'text', label: 'テキスト' },
  { value: '3d', label: '3D' }
];

const MODEL_FILE_TYPES = [
  { value: 'checkpoint', label: 'Checkpoint' },
  { value: 'lora', label: 'LoRa' },
  { value: 'locon', label: 'LoCon' },
  { value: 'lycoris', label: 'LyCORIS' },
  { value: 'svd', label: 'SVD' },
  { value: 'vae', label: 'VAE' },
  { value: 'controlnet', label: 'ControlNet' },
  { value: 'textual_inversion', label: 'Textual Inversion' },
  { value: 'hypernetwork', label: 'Hypernetwork' },
  { value: 'embedding', label: 'Embedding' },
  { value: 'upscaler', label: 'Upscaler' },
];

// ファイルごとのメタデータ選択肢
const FILE_TYPE_SELECT_OPTIONS = [
  { value: 'checkpoint', label: 'Checkpoint' },
  { value: 'lora', label: 'LoRA' },
  { value: 'locon', label: 'LoCon' },
  { value: 'lycoris', label: 'LyCORIS' },
  { value: 'svd', label: 'SVD' },
  { value: 'vae', label: 'VAE' },
  { value: 'controlnet', label: 'ControlNet' },
  { value: 'embedding', label: 'Embedding' },
  { value: 'hypernetwork', label: 'Hypernetwork' },
  { value: 'textual_inversion', label: 'Textual Inversion' },
  { value: 'upscaler', label: 'Upscaler' },
  { value: 'other', label: 'その他' },
];
const MODEL_SIZE_OPTIONS = [
  { value: 'full', label: 'Full' },
  { value: 'pruned', label: 'Pruned' },
  { value: 'other', label: 'その他' },
];
const PRECISION_OPTIONS = [
  { value: 'fp16', label: 'fp16' },
  { value: 'fp32', label: 'fp32' },
  { value: 'bf16', label: 'bf16' },
  { value: 'fp8', label: 'fp8' },
  { value: 'nf4', label: 'nf4' },
  { value: 'other', label: 'その他' },
];

const AGE_LIMIT_OPTIONS = [
  { value: 1, label: '全年齢' },
  { value: 2, label: 'PG-12' },
  { value: 3, label: 'R-15' },
  { value: 4, label: 'R-18' },
  { value: 5, label: 'R-18G' },
];

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ModelUploadForm = () => {
  const { model_id } = useParams();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [tags, setTags] = useState(['anime', 'portrait', 'kawaii']);
  const [newTag, setNewTag] = useState('');
  const [modelFiles, setModelFiles] = useState({});
  const [sampleImages, setSampleImages] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [versions, setVersions] = useState([
    {
      id: 1,
      name: 'v1.0',
      description: '初期バージョン',
      categories: 'image',
      triggerWords: ['portrait', 'kawaii'],
      status: 'published',
      updated: '2024-03-15',
      specs: {
        modelType: 'standard',
        baseModel: 'sd_1_5',
        epochs: '10',
        steps: '1000',
        clipSkip: '2',
        vae: '',
      }
    }
  ]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedModelFileType, setSelectedModelFileType] = useState('');
  const [source, setSource] = useState('original');
  const [baseInfo, setBaseInfo] = useState({
    name: '',
    description: '',
    category: '',
    model_type: '',
    status: '',
    source: '',
    age_limit: 1,
    usage_permission_generate: false,
    usage_permission_online_training: false,
    usage_permission_no_credit: false,
    usage_permission_derivative: false,
    usage_permission_derivative_license: false,
    usage_permission_redistribute: false,
    commercial_license_no_rights: false,
    commercial_license_service: false,
    commercial_license_derivative: false,
    downloadable: true,
  });
  const [licenseCandidates, setLicenseCandidates] = useState([]);
  const [customLicenseUrl, setCustomLicenseUrl] = useState("");

  // ファイルごとのメタデータ状態
  const [fileMetas, setFileMetas] = useState({});

  const api = new ApiFetch();

  useEffect(() => {
    if (model_id) {
      // モデル情報をAPIから取得して編集モードへ
      api.get(ApiFetch.API_PROTOCOL + "//" + ApiFetch.API_HOST + `/v1/model/info/${model_id}/edit`)
        .then(response => response.json())
        .then(data => {
          // upload_statusがundefinedの場合は0をセット
          const versionsWithStatus = (data.data.versions || []).map(v => ({
            ...v,
            specs: {
              ...v.specs,
              upload_status: v.specs && typeof v.specs.upload_status !== 'undefined' ? v.specs.upload_status : 0
            }
          }));
          setVersions(versionsWithStatus);
          setSelectedVersion(versionsWithStatus[0]?.id);
          setBaseInfo(data.data.base_info);
          setLicenseCandidates(data.data.license_candidates || []);
          setCustomLicenseUrl(data.data.base_info.license_url || "");
        })
        .catch(error => {
          console.error('モデル情報の取得に失敗しました:', error);
        });
    } else {
      // 新規登録として新たにモデルIDをAPIから取得
      api.post(ApiFetch.API_PROTOCOL + "//" + ApiFetch.API_HOST + '/v1/model/new')
        .then(response => response.json())
        .then(data => {
          if (data.result) {
            navigate(`/upload/model/${data.data.model_id}`);
          }
        })
        .catch(error => {
          console.error('新規モデルIDの取得に失敗しました:', error);
        });
    }
  }, [model_id, navigate]);

  // バージョン情報の更新関数（遅延更新版）
  const updateVersionSpec = (versionId, field, value) => {
    const updatedVersions = versions.map(v =>
      v.id === versionId
        ? { ...v, specs: { ...v.specs, [field]: value } }
        : v
    );
    setVersions(updatedVersions);
  };

  // バージョンの説明の更新関数
  const updateVersionDescription = (versionId, value) => {
    const updatedVersions = versions.map(v =>
      v.id === versionId
        ? { ...v, description: value }
        : v
    );
    setVersions(updatedVersions);
  };

  // 推奨情報の更新関数
  const updateRecommendedSpec = (versionId, field, value) => {
    const updatedVersions = versions.map(v =>
      v.id === versionId
        ? { ...v, recommended_settings: { ...v.recommended_settings, [field]: value } }
        : v
    );
    setVersions(updatedVersions);
  };

  // フォーム入力用の一時状態を管理
  const [formInputs, setFormInputs] = useState({});
  
  // 入力中の値を一時的に保存
  const handleFormInputChange = (versionId, field, value) => {
    setFormInputs(prev => ({
      ...prev,
      [versionId]: {
        ...prev[versionId],
        [field]: value
      }
    }));
  };
  
  // 入力完了時（blur時）に実際の値を更新
  const handleFormInputBlur = (versionId, field) => {
    if (formInputs[versionId] && formInputs[versionId][field] !== undefined) {
      updateVersionSpec(versionId, field, formInputs[versionId][field]);
    }
  };
  
  // バージョン名更新用の関数
  const handleVersionNameChange = (versionId, value) => {
    setFormInputs(prev => ({
      ...prev,
      [versionId]: {
        ...prev[versionId],
        name: value
      }
    }));
  };
  
  // バージョン名の確定
  const handleVersionName = (versionId,name) => {
      const updatedVersions = versions.map(v =>
        v.id === versionId ? { ...v, name: name } : v
      );
      setVersions(updatedVersions);
  };

  // ドラッグ&ドロップ処理
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const simulateUploadProgress = (versionId, fileKey) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 30;
      if (progress > 100) {
        progress = 100;
        clearInterval(interval);
      }
      setUploadProgress(prev => ({
        ...prev,
        [versionId]: {
          ...prev[versionId],
          [fileKey]: Math.min(progress, 100)
        }
      }));
    }, 500);
  };

  // タグ管理
  const handleAddTag = (e) => {
    e.preventDefault();
    if (newTag && !tags.includes(newTag)) {
      setTags([...tags, newTag]);
      setNewTag('');
    }
  };

  const handleDeleteTag = (tagToDelete) => {
    setTags(tags.filter(tag => tag !== tagToDelete));
  };

  const handleModelFileUpload = async (versionId, event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.pt') && !file.name.endsWith('.ckpt') && !file.name.endsWith('.safetensors')) {
        alert('サポートされていないファイル形式です。.pt、.ckpt、または.safetensorsファイルを選択してください。');
        return;
      }
      if (file.size > 30 * 1024 * 1024 * 1024) {
        alert('ファイルサイズが大きすぎます。30GB以下のファイルを選択してください。');
        return;
      }
      try {
        // 1. addFileでfileIdを取得
        const addFileRes = await api.post(
          ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/addFile`,
          {
            versionId: versionId,
            fileName: file.name,
            fileType: file.type || null,
            fileSize: file.size
          }
        );
        const addFileData = await addFileRes.json();
        if (!addFileData.result || !addFileData.fileId) {
          alert('ファイル登録に失敗しました');
          return;
        }
        const fileId = addFileData.fileId;
        // 2. アップロード用URLをfileIdで取得
        const urlRes = await api.post(
          ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/generateModelUploadUrl`,
          {
            versionId: versionId,
            fileId: fileId
          }
        );
        const urlData = await urlRes.json();
        if (!urlData.result || !urlData.signedUrl) {
          alert('アップロードURLの取得に失敗しました');
          return;
        }
        const signedUploadUrl = urlData.signedUrl;
        // 3. PUTでアップロード
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', signedUploadUrl, true);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const percent = Math.round((e.loaded / e.total) * 100);
            setUploadProgress(prev => ({
              ...prev,
              [versionId]: {
                ...prev[versionId],
                model: percent
              }
            }));
          }
        };
        xhr.onload = async () => {
          if (xhr.status === 200 || xhr.status === 201) {
            alert('ファイルが正常にアップロードされました。');
            // 4. upload_status=1に更新
            await api.put(
              ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/updateFileUploadStatus/${fileId}`,
              { upload_status: 1 }
            );
            // ファイル一覧を再取得
            const res2 = await api.get(ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/getFiles/${versionId}`);
            const data2 = await res2.json();
            if (data2.result) {
              setModelFileList(prev => ({ ...prev, [versionId]: data2.files }));
            }
          } else {
            alert('ファイルのアップロードに失敗しました。');
          }
        };
        xhr.onerror = () => {
          alert('アップロード中にエラーが発生しました。');
        };
        xhr.send(file);
        setModelFiles({
          ...modelFiles,
          [versionId]: file
        });
        setUploadProgress(prev => ({
          ...prev,
          [versionId]: {
            ...prev[versionId],
            model: 0
          }
        }));
      } catch (error) {
        alert('アップロード処理でエラーが発生しました。');
        console.error(error);
      }
    }
  };

  // ファイルのドラッグ&ドロップや選択時にメタデータ入力欄を表示
  const handleModelFileDrop = (versionId, e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      // 複数ファイル対応
      const newMetas = { ...fileMetas };
      files.forEach(file => {
        newMetas[file.name] = {
          fileTypeSelect: '',
          modelSize: '',
          precision: '',
          file
        };
      });
      setFileMetas(newMetas);
    }
  };

  const handleModelFileSelect = (versionId, e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const newMetas = { ...fileMetas };
      files.forEach(file => {
        newMetas[file.name] = {
          fileTypeSelect: '',
          modelSize: '',
          precision: '',
          file
        };
      });
      setFileMetas(newMetas);
    }
  };

  // メタデータ選択変更
  const handleMetaChange = (fileName, field, value) => {
    setFileMetas(prev => ({
      ...prev,
      [fileName]: {
        ...prev[fileName],
        [field]: value
      }
    }));
  };

  // ファイルサイズを人間可読形式で表示
  const humanFileSize = (size) => {
    if (!size) return '-';
    const i = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    return (size / Math.pow(1024, i)).toFixed(1) + ' ' + sizes[i];
  };

  // サポート拡張子
  const ALLOWED_IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif'];

  // サンプル画像DBリスト取得
  const fetchSampleImages = async (versionId) => {
    const res = await api.get(ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/getSampleImages/${versionId}`);
    const data = await res.json();
    if (data.result && Array.isArray(data.images)) {
      setSampleImages(prev => {
        // DB画像
        const dbImages = data.images.reduce((acc, img) => {
          acc[img.id] = { ...img, status: 'done' };
          return acc;
        }, {});
        // ローカルpending/uploading/error画像のみ残し、deleting: trueは除外
        const localImages = prev[versionId]
          ? Object.entries(prev[versionId])
              .filter(([k, v]) => k.startsWith('local_') && (v.status === 'pending' || v.status === 'uploading' || v.status === 'error' || v.deleting))
              .reduce((acc, [k, v]) => { acc[k] = v; return acc; }, {})
          : {};
        return { ...prev, [versionId]: { ...dbImages, ...localImages } };
      });
    }
  };

  // バージョン選択時にサンプル画像リスト取得
  useEffect(() => {
    if (selectedVersion) fetchSampleImages(selectedVersion);
    // eslint-disable-next-line
  }, [selectedVersion]);

  // サンプル画像アップロード
  const handleSampleImagesUpload = async (versionId, event) => {
    const files = Array.from(event.target.files);
    // 既存サンプル画像数を取得
    const currentCount = sampleImages[versionId] ? Object.keys(sampleImages[versionId]).length : 0;
    if (currentCount + files.length > 20) {
      alert('サンプル画像は最大20枚までです。既存画像数と合わせて20枚以内にしてください。');
      return;
    }
    const validFiles = files.filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!ALLOWED_IMAGE_EXTS.includes(ext)) {
        alert('許可されていない画像形式です: ' + file.name);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert('画像サイズは10MB以下にしてください: ' + file.name);
        return false;
      }
      return true;
    }).slice(0, 20 - currentCount);
    if (validFiles.length === 0) return;
    // 1件ずつ直列アップロード
    // まず全てpendingでセット
    const tempIds = [];
    validFiles.forEach(file => {
      const tempId = 'local_' + file.name + '_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
      tempIds.push({ tempId, file });
      setSampleImages(prev => ({
        ...prev,
        [versionId]: {
          ...(prev[versionId] || {}),
          [tempId]: { file, status: 'pending', progress: 0 }
        }
      }));
    });
    // 1件ずつアップロード
    for (const { tempId, file } of tempIds) {
      // pending→uploading
      setSampleImages(prev => ({
        ...prev,
        [versionId]: {
          ...prev[versionId],
          [tempId]: { ...prev[versionId][tempId], status: 'uploading', progress: 0 }
        }
      }));
      const form = new FormData();
      form.append('versionId', versionId);
      form.append('modelId', model_id);
      form.append('file', file);
      // 進捗管理
      await new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + '/v1/model/uploadSampleImage', true);
        xhr.withCredentials = true;
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const percent = Math.round((e.loaded / e.total) * 100);
            setSampleImages(prev => ({ ...prev, [versionId]: { ...prev[versionId], [tempId]: { ...prev[versionId][tempId], progress: percent } } }));
          }
        };
        xhr.onload = () => {
          if (xhr.status === 200) {
            try {
              const res = JSON.parse(xhr.responseText);
              if (res.result && res.fileId) {
                // DB画像として再取得
                fetchSampleImages(versionId);
                setSampleImages(prev => {
                  const newList = { ...prev[versionId] };
                  delete newList[tempId];
                  return { ...prev, [versionId]: newList };
                });
                resolve();
              } else {
                setSampleImages(prev => ({ ...prev, [versionId]: { ...prev[versionId], [tempId]: { ...prev[versionId][tempId], status: 'error', error: res.error || 'アップロード失敗' } } }));
                resolve();
              }
            } catch {
              setSampleImages(prev => ({ ...prev, [versionId]: { ...prev[versionId], [tempId]: { ...prev[versionId][tempId], status: 'error', error: 'アップロード失敗' } } }));
              resolve();
            }
          } else {
            setSampleImages(prev => ({ ...prev, [versionId]: { ...prev[versionId], [tempId]: { ...prev[versionId][tempId], status: 'error', error: 'アップロード失敗' } } }));
            resolve();
          }
        };
        xhr.onerror = () => {
          setSampleImages(prev => ({ ...prev, [versionId]: { ...prev[versionId], [tempId]: { ...prev[versionId][tempId], status: 'error', error: '通信エラー' } } }));
          resolve();
        };
        xhr.send(form);
      });
    }
  };

  // タブ変更時の処理
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // タブ2（バージョン管理）に遷移する際、選択中のバージョンがない場合は最初のバージョンを選択
    if (newValue === 1 && versions.length > 0 && !selectedVersion) {
      setSelectedVersion(versions[0].id);
    }
  };

  // バージョン選択時の処理
  const handleVersionSelect = (versionId) => {
    setSelectedVersion(versionId);
    // サンプル画像とファイルのリストを取得
    fetchSampleImages(versionId);
    api.get(ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/getFiles/${versionId}`)
      .then(res => res.json())
      .then(data => {
        if (data.result) {
          setModelFileList(prev => ({ ...prev, [versionId]: data.files }));
        }
      });
  };

  // 公開状態のバージョンがあるか確認
  const hasPublishedVersion = versions.some(version => version.status === 'published');

  // モデル公開処理
  const handleModelPublish = async () => {
    if (!hasPublishedVersion) {
      alert('少なくとも1つのバージョンが公開状態である必要があります。');
      return;
    }
    // 各バージョンの公開処理
    for (const version of versions) {
      if (version.status !== 'published') {
        try {
          const res = await api.put(
            ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/publish/${version.id}`,
            {}
          );
          const data = await res.json();
          if (data.result) {
            alert(`バージョン ${version.name} を公開しました`);
          } else {
            alert(`バージョン ${version.name} の公開に失敗しました: ${data.error || '不明なエラー'}`);
          }
        } catch (e) {
          alert(`バージョン ${version.name} の公開時にエラーが発生しました: ${e.message}`);
        }
      }
    }
  };

  const ModelSpecsForm = ({ version }) => (
    <Box sx={{ py: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="バージョン名"
            required
            defaultValue={formInputs[version.id]?.name ?? version.name}
            onBlur={(e) => {
              handleFormInputChange(version.id, 'name', e.target.value)
              handleVersionName(version.id, e.target.value);
            }}
            placeholder="例: v1.0.0"
          />
        </Grid>

        {/* 新しいダウンロード可能オプションフィールド */}
        <Grid item xs={6}>
          <FormControl fullWidth required>
            <InputLabel>モデルダウンロード可能</InputLabel>
            <Select
              value={formInputs[version.id]?.downloadable ?? version.specs.downloadable}
              label="モデルダウンロード可能"
              onChange={e => {
                handleFormInputChange(version.id, 'downloadable', e.target.value)
                updateVersionSpec(version.id, 'downloadable', e.target.value);
              }}
            >
              <MenuItem value={true}>はい</MenuItem>
              <MenuItem value={false}>いいえ</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={6}>
          <FormControl fullWidth required>
            <InputLabel>モデル使用タイプ</InputLabel>
            <Select
              defaultValue={formInputs[version.id]?.modelUseType ?? version.specs.modelUseType}
              label="モデル使用タイプ"
              onChange={e => {
                handleFormInputChange(version.id, 'modelUseType', e.target.value)
                updateVersionSpec(version.id, 'modelUseType', e.target.value);
              }}
            >
              {MODEL_TYPES.map(type => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={6}>
          <FormControl fullWidth required>
            <InputLabel>ベースモデル</InputLabel>
            <Select
              value={formInputs[version.id]?.baseModel ?? version.specs.baseModel}
              label="ベースモデル"
              onChange={e => {
                handleFormInputChange(version.id, 'baseModel', e.target.value)
                updateVersionSpec(version.id, 'baseModel', e.target.value);
              }}
            >
              {BASE_MODELS.map(model => (
                <MenuItem key={model.value} value={model.value}>
                  {model.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={6}>
          <TextField
            fullWidth
            type="number"
            label="学習ステップ数"
            defaultValue={formInputs[version.id]?.steps ?? version.specs.steps}
            onBlur={(e) => {
              handleFormInputChange(version.id, 'steps', e.target.value)
              updateVersionSpec(version.id, 'steps', e.target.value);
            }}
            placeholder="例: 1000"
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            fullWidth
            type="number"
            label="Epochs"
            defaultValue={formInputs[version.id]?.epochs ?? version.specs.epochs}
            onBlur={(e) => {
              handleFormInputChange(version.id, 'epochs', e.target.value)
              updateVersionSpec(version.id, 'epochs', e.target.value);
            }}
            placeholder="例: 10"
          />
        </Grid>

        <Grid item xs={6}>
          <FormControl fullWidth required>
            <InputLabel>スタイル</InputLabel>
            <Select
              value={formInputs[version.id]?.style ?? version.specs.style}
              label="Style"
              onChange={e => {
                handleFormInputChange(version.id, 'style', e.target.value)
                updateVersionSpec(version.id, 'style', e.target.value);
              }}
            >
              {STYLES.map(style => (
                <MenuItem key={style.value} value={style.value}>
                  {style.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h5" component="h5" gutterBottom>
            推奨パラメーター
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <TextField
            fullWidth
            label="VAE"
            defaultValue={formInputs[version.id]?.recommendedVae ?? version.recommended_settings.vae}
            onBlur={(e) => {
              handleFormInputChange(version.id, 'recommendedVae', e.target.value)
              updateRecommendedSpec(version.id, 'vae', e.target.value);
            }}
            placeholder="推奨VAEを入力"
          />
        </Grid>

        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel>Clip Skip</InputLabel>
            <Select
              value={formInputs[version.id]?.recommendedClipSkip ?? version.recommended_settings.clipSkip}
              label="Clip Skip"
              onChange={e => {
                handleFormInputChange(version.id, 'recommendedClipSkip', e.target.value)
                updateRecommendedSpec(version.id, 'clipSkip', e.target.value);
              }}
            >
              {CLIP_SKIP_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Negative Prompt"
            defaultValue={formInputs[version.id]?.recommendedNegativePrompt ?? version.recommended_settings.negativePrompt}
            onBlur={(e) => {
              handleFormInputChange(version.id, 'recommendedNegativePrompt', e.target.value)
              updateRecommendedSpec(version.id, 'negativePrompt', e.target.value);
            }}
            placeholder="例: low quality, blurry"
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            fullWidth
            type="number"
            label="CFG Scale"
            defaultValue={formInputs[version.id]?.recommendedCfgScale ?? version.recommended_settings.cfgScale}
            onBlur={(e) => {
              handleFormInputChange(version.id, 'recommendedCfgScale', e.target.value)
              updateRecommendedSpec(version.id, 'cfgScale', e.target.value);
            }}
            placeholder="例: 7.5"
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Sampler"
            defaultValue={formInputs[version.id]?.recommendedSampler ?? version.recommended_settings.sampler}
            onBlur={(e) => {
              handleFormInputChange(version.id, 'recommendedSampler', e.target.value)
              updateRecommendedSpec(version.id, 'sampler', e.target.value);
            }}
            placeholder="例: Euler a"
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            fullWidth
            type="number"
            label="ステップ数"
            defaultValue={formInputs[version.id]?.recommendedSteps ?? version.recommended_settings.steps}
            onBlur={(e) => {
              handleFormInputChange(version.id, 'recommendedSteps', e.target.value)
              updateRecommendedSpec(version.id, 'steps', e.target.value);
            }}
            placeholder="例: 28"
          />
        </Grid>
      </Grid>
    </Box>
  );

  // 追加: 保存処理関数
  const handleSaveLicenseAndPermissions = async () => {
    if (!model_id) {
      alert('モデルIDがありません。');
      return;
    }
    try {
      const res = await api.put(
        ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/info/${model_id}`,
        baseInfo
      );
      const data = await res.json();
      if (data.result) {
        alert('利用許諾範囲とライセンスを保存しました');
      } else {
        alert('保存に失敗しました: ' + (data.error || '不明なエラー'));
      }
    } catch (e) {
      alert('保存時にエラーが発生しました: ' + e.message);
    }
  };

  // バージョン公開処理
  const handlePublishVersion = async () => {
    const version = versions.find(v => v.id === selectedVersion);
    if (!version) {
      alert('バージョンが選択されていません');
      return;
    }
    if (version.specs.upload_status !== 1 && version.specs.upload_status !== '1') {
      alert('モデルファイルがアップロードされていません。アップロード後に公開できます。');
      return;
    }
    try {
      const res = await api.put(
        ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/publish/${version.id}`,
        {}
      );
      const data = await res.json();
      if (data.result) {
        alert('バージョンを公開しました');
        // 公開後の状態を反映（再取得など）
        api.get(ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/info/${model_id}/edit`)
          .then(response => response.json())
          .then(data => {
            setVersions(data.data.versions);
          });
      } else {
        alert('公開に失敗しました: ' + (data.errors || '不明なエラー'));
      }
    } catch (e) {
      alert('公開時にエラーが発生しました: ' + e.message);
    }
  };

  // モデルバージョンごとのファイル一覧管理
  const [modelFileList, setModelFileList] = useState({});

  // バージョン選択時にファイル一覧を取得
  useEffect(() => {
    if (selectedVersion) {
      api.get(ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/getFiles/${selectedVersion}`)
        .then(res => res.json())
        .then(data => {
          if (data.result) {
            setModelFileList(prev => ({ ...prev, [selectedVersion]: data.files }));
          }
        });
    }
  }, [selectedVersion]);

  // ファイル削除
  const handleDeleteModelFile = async (fileId) => {
    if (!window.confirm('本当にこのファイルを削除しますか？')) return;
    try {
      const res = await api.delete(ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/deleteFile/${fileId}`);
      const data = await res.json();
      if (data.result) {
        // モデル基本情報を再取得
        try {
          const infoRes = await api.get(ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/info/${model_id}/edit`);
          const infoData = await infoRes.json();
          if (infoData.result && infoData.data) {
            setBaseInfo(infoData.data.base_info);
            setVersions(infoData.data.versions);
          }
        } catch (e) {
          // 取得失敗時は何もしない
        }
        // 削除後、ファイル一覧を再取得
        const res2 = await api.get(ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/getFiles/${selectedVersion}`);
        const data2 = await res2.json();
        if (data2.result) {
          setModelFileList(prev => ({ ...prev, [selectedVersion]: data2.files }));
        }
      } else {
        alert('ファイル削除に失敗しました: ' + (data.error || '不明なエラー'));
      }
    } catch (e) {
      alert('ファイル削除時にエラーが発生しました: ' + e.message);
    }
  };

  const [uploading, setUploading] = useState(false); // アップロード中フラグ追加
  const [previewImage, setPreviewImage] = useState(null);

  return (
    <div className='wrapper'>
            <Header />
      <Category />
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          モデルを公開
        </Typography>
        <Typography color="text.secondary">
          必要な情報を入力して、あなたのAIモデルを共有しましょう
        </Typography>
      </Box>

      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
          >
            <Tab icon={<Info />} label="基本情報" />
            <Tab icon={<Settings />} label="バージョン管理" />
            <Tab icon={<Label />} label="利用規約" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  fullWidth
                  required
                  label="モデル名"
                  placeholder="あなたのモデルの名前"
                  helperText="ユーザーが見つけやすい、分かりやすい名前をつけましょう"
                  value={baseInfo.name}
                  onChange={(e) => setBaseInfo({ ...baseInfo, name: e.target.value })}
                />

                <Box>
                  <FormControl fullWidth required>
                    <InputLabel>カテゴリー</InputLabel>
                    <Select
                      value={baseInfo.category}
                      label="カテゴリー"
                      onChange={(e) => setBaseInfo({ ...baseInfo, category: e.target.value })}
                    >
                      {CATEGORIES.map((category) => (
                        <MenuItem key={category.value} value={category.value}>
                          {category.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <FormControl fullWidth required>
                    <InputLabel>タイプ</InputLabel>
                    <Select
                      value={baseInfo.model_type}
                      label="タイプ"
                      onChange={(e) => setBaseInfo({ ...baseInfo, model_type: e.target.value })}
                    >
                      {MODEL_FILE_TYPES.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ display: 'none' }}>
                  <Typography variant="subtitle1" gutterBottom>
                    タグ設定
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        onDelete={() => handleDeleteTag(tag)}
                        sx={{ m: 0.5 }}
                      />
                    ))}
                  </Box>
                  <TextField
                    fullWidth
                    label="新しいタグを追加"
                    placeholder="タグを入力してEnterを押してください"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleAddTag(e);
                      }
                    }}
                  />
                </Box>

                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="説明"
                  placeholder="このモデルの特徴、使い方、注意点などを説明してください"
                  value={baseInfo.description}
                  onChange={(e) => setBaseInfo({ ...baseInfo, description: e.target.value })}
                />
                
                <Box>
                  <FormControl fullWidth required>
                    <InputLabel>年齢制限</InputLabel>
                    <Select
                      value={baseInfo.age_limit}
                      label="年齢制限"
                      onChange={(e) => setBaseInfo({ ...baseInfo, age_limit: e.target.value })}
                    >
                      {AGE_LIMIT_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    出典
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="source"
                      name="source"
                      value={baseInfo.source}
                      onChange={(e) => setBaseInfo({ ...baseInfo, source: e.target.value })}
                    >
                      <FormControlLabel value="original" control={<Radio />} label="オリジナル" />
                      <FormControlLabel value="exclusive" control={<Radio />} label="オリジナル アワートAI専属公開" />
                      <FormControlLabel value="repost" control={<Radio />} label="転載(モデルの制作者以外が投稿)" />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* 基本情報を保存ボタン追加 */}
                <Box sx={{ textAlign: 'right', mt: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={async () => {
                      try {
                        const res = await api.put(
                          ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/info/${model_id}`,
                          baseInfo
                        );
                        const data = await res.json();
                        if (data.result) {
                          alert('基本情報を保存しました');
                        } else {
                          alert('保存に失敗しました: ' + (data.errors ? data.errors.join(',') : ''));
                        }
                      } catch (e) {
                        alert('保存時にエラーが発生しました');
                      }
                    }}
                  >
                    基本情報を保存
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card>
                <Box sx={{ p: 2, bgcolor: 'grey.50', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1">バージョン管理</Typography>
                  <Button
                    startIcon={<Add />}
                    onClick={async () => {
                      // バックエンドで新規バージョン作成
                      try {
                        const res = await api.post(
                          ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/addVersion/${model_id}`,
                          {}
                        );
                        const data = await res.json();
                        if (data.result && data.version_id) {
                          // バージョンリストを再取得
                          const infoRes = await api.get(ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/info/${model_id}/edit`);
                          const infoData = await infoRes.json();
                          setVersions(infoData.data.versions);
                          setSelectedVersion(data.version_id);
                        } else {
                          alert('新規バージョンの作成に失敗しました: ' + (data.error || '不明なエラー'));
                        }
                      } catch (e) {
                        alert('新規バージョン作成時にエラーが発生しました: ' + e.message);
                      }
                    }}
                  >
                    新規バージョン
                  </Button>
                </Box>
                <List>
                  {versions.map((version) => (
                    <ListItem
                      key={version.id}
                      button
                      selected={selectedVersion === version.id}
                      onClick={() => handleVersionSelect(version.id)}
                    >
                      <ListItemText
                        primary={version.name}
                        secondary={version.description || '説明なし'}
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" onClick={async (e) => {
                          e.stopPropagation();
                          if(window.confirm('本当にこのバージョンを削除しますか？')) {
                            try {
                              const res = await api.delete(ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/deleteVersion/${version.id}`);
                              const data = await res.json();
                              if(data.result) {
                                // バージョンリストを再取得
                                const infoRes = await api.get(ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/info/${model_id}/edit`);
                                const infoData = await infoRes.json();
                                setVersions(infoData.data.versions);
                                if(selectedVersion === version.id && infoData.data.versions.length > 0) {
                                  setSelectedVersion(infoData.data.versions[0].id);
                                }
                              } else {
                                alert('削除に失敗しました: ' + (data.error || '不明なエラー'));
                              }
                            } catch (e) {
                              alert('削除時にエラーが発生しました: ' + e.message);
                            }
                          }
                        }}>
                          <Close />
                        </IconButton>
                        <IconButton edge="end">
                          <ChevronRight />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Card>
            </Grid>

            <Grid item xs={12} md={8}>
              {selectedVersion && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Card>
                    <CardContent>
                      <ModelSpecsForm version={versions.find(v => v.id === selectedVersion)} />
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          required
                          label="バージョンの説明"
                          placeholder="このバージョンでの変更点や特徴を説明してください"
                          defaultValue={formInputs[selectedVersion]?.description || versions.find(v => v.id === selectedVersion)?.description || ''}
                          onChange={(e) => {
                            handleFormInputChange(selectedVersion, 'description', e.target.value)
                          }}
                          onBlur={(e) => updateVersionDescription(selectedVersion, e.target.value)}
                        />

                        {/* モデルファイルのアップロード */}
                        <Box>
                          <Typography variant="subtitle1" gutterBottom>
                            モデルファイル
                          </Typography>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 3,
                              backgroundColor: 'grey.50',
                              textAlign: 'center',
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: 'grey.100'
                              }
                            }}
                            onDrop={e => handleModelFileDrop(selectedVersion, e)}
                            onDragOver={handleDragOver}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onClick={(e) => {
                              // ファイル選択ダイアログを開く
                              document.getElementById(`model-file-upload-${selectedVersion}`).click()
                            }}
                          >
                            <input
                              type="file"
                              accept=".ckpt,.safetensors,.pt,.pth,.aivm,.aivmx"
                              style={{ display: 'none' }}
                              id={`model-file-upload-${selectedVersion}`}
                              multiple
                              onChange={e => handleModelFileSelect(selectedVersion, e)}
                            />
                            <label >
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 , cursor: 'pointer'}}>
                                <Upload />
                                <Typography>
                                  モデルファイルをドロップ、または選択してください
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  対応形式: .ckpt, .safetensors, .pt, .pth, .aivm, .aivmx
                                </Typography>
                              </Box>
                            </label>
                          </Paper>
                          {/* 既存ファイル一覧表示 */}
                          {modelFileList[selectedVersion] && modelFileList[selectedVersion].length > 0 && (
                            <List dense>
                              {modelFileList[selectedVersion].map(file => (
                                <ListItem key={file.id} secondaryAction={
                                  <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteModelFile(file.id)}>
                                    <Close />
                                  </IconButton>
                                }>
                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {file.upload_status === 1 ? (
                                          <CheckCircle color="success" fontSize="small" />
                                        ) : (
                                          <Error color='warning' fontSize="small" />
                                        )}
                                        {file.file_name}
                                      </Box>
                                    }
                                    secondary={`種類: ${file.file_type || '-'} ／ サイズ: ${humanFileSize(file.file_size)} ／ ファイル種別: ${file.file_type_select || '-'} ／ モデルサイズ: ${file.model_size || '-'} ／ 精度: ${file.precision || '-'}`}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          )}
                          {/* プログレスバー表示部分（モデルファイルアップロード欄の下に追加） */}
                          {uploadProgress[selectedVersion] && typeof uploadProgress[selectedVersion].model === 'number' && (
                            <Box sx={{ mt: 1 }}>
                              <LinearProgress variant="determinate" value={uploadProgress[selectedVersion].model} />
                              <Typography variant="caption">アップロード進捗: {uploadProgress[selectedVersion].model}%</Typography>
                            </Box>
                          )}
                        </Box>

                        {/* ここからファイルごとのメタデータ選択UI・アップロードボタン・アップロード済み表示・バリデーション・アップロード処理を追加 */}
                        {Object.values(fileMetas).length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2">アップロードするファイルとメタデータ</Typography>
                            <List>
                              {Object.values(fileMetas).map(meta => (
                                <ListItem
                                  key={meta.file.name}
                                  alignItems="flex-start"
                                  sx={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 2,
                                    mb: 2,
                                    p: 2,
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 2,
                                    alignItems: 'center'
                                  }}
                                >
                                  <ListItemText
                                    primary={meta.file.name}
                                    secondary={`サイズ: ${humanFileSize(meta.file.size)}`}
                                    sx={{ minWidth: 180, maxWidth: 240 }}
                                  />
                                  <FormControl sx={{ minWidth: 120, mr: 1, border: '1px solid #e0e0e0', borderRadius: 1, background: '#fafafa', p: 1 }} size="small">
                                    <InputLabel>ファイル種別</InputLabel>
                                    <Select
                                      value={meta.fileTypeSelect}
                                      label="ファイル種別"
                                      onChange={e => handleMetaChange(meta.file.name, 'fileTypeSelect', e.target.value)}
                                    >
                                      {FILE_TYPE_SELECT_OPTIONS.map(opt => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  <FormControl sx={{ minWidth: 100, mr: 1, border: '1px solid #e0e0e0', borderRadius: 1, background: '#fafafa', p: 1 }} size="small">
                                    <InputLabel>モデルサイズ</InputLabel>
                                    <Select
                                      value={meta.modelSize}
                                      label="モデルサイズ"
                                      onChange={e => handleMetaChange(meta.file.name, 'modelSize', e.target.value)}
                                    >
                                      {MODEL_SIZE_OPTIONS.map(opt => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  <FormControl sx={{ minWidth: 100, mr: 1, border: '1px solid #e0e0e0', borderRadius: 1, background: '#fafafa', p: 1 }} size="small">
                                    <InputLabel>精度</InputLabel>
                                    <Select
                                      value={meta.precision}
                                      label="精度"
                                      onChange={e => handleMetaChange(meta.file.name, 'precision', e.target.value)}
                                    >
                                      {PRECISION_OPTIONS.map(opt => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  {/* プログレスバー表示 */}
                                  {uploading && uploadProgress[meta.file.name] !== undefined && (
                                    <Box sx={{ minWidth: 120, mr: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <LinearProgress variant="determinate" value={uploadProgress[meta.file.name]} sx={{ width: 80 }} />
                                      <Typography variant="caption">{uploadProgress[meta.file.name]}%</Typography>
                                    </Box>
                                  )}
                                </ListItem>
                              ))}
                            </List>
                            <Button
                              variant="contained"
                              color="primary"
                              disabled={uploading || Object.values(fileMetas).length === 0 || Object.values(fileMetas).some(meta => !meta.fileTypeSelect || !meta.modelSize || !meta.precision)}
                              onClick={async () => {
                                setUploading(true);
                                const newProgress = {};
                                try {
                                  for (const meta of Object.values(fileMetas)) {
                                    // addFileでfileId取得
                                    const addFileRes = await api.post(
                                      ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/addFile`,
                                      {
                                        versionId: selectedVersion,
                                        fileName: meta.file.name,
                                        fileType: meta.file.type || null,
                                        fileSize: meta.file.size,
                                        fileTypeSelect: meta.fileTypeSelect,
                                        modelSize: meta.modelSize,
                                        precision: meta.precision
                                      }
                                    );
                                    const addFileData = await addFileRes.json();
                                    if (!addFileData.result || !addFileData.fileId) {
                                      alert('ファイル登録に失敗しました: ' + meta.file.name);
                                      continue;
                                    }
                                    const fileId = addFileData.fileId;
                                    // アップロードURL取得
                                    const urlRes = await api.post(
                                      ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/generateModelUploadUrl`,
                                      {
                                        modelId: model_id,
                                        versionId: selectedVersion,
                                        fileId: fileId
                                      }
                                    );
                                    const urlData = await urlRes.json();
                                    if (!urlData.result || !urlData.signedUrl) {
                                      alert('アップロードURL取得失敗: ' + meta.file.name);
                                      continue;
                                    }
                                    // PUTアップロード
                                    await new Promise((resolve, reject) => {
                                      const xhr = new XMLHttpRequest();
                                      xhr.open('PUT', urlData.signedUrl, true);
                                      xhr.setRequestHeader('Content-Type', meta.file.type || 'application/octet-stream');
                                      xhr.upload.onprogress = (e) => {
                                        if (e.lengthComputable) {
                                          const percent = Math.round((e.loaded / e.total) * 100);
                                          setUploadProgress(prev => ({ ...prev, [meta.file.name]: percent }));
                                        }
                                      };
                                      xhr.onload = async () => {
                                        if (xhr.status === 200 || xhr.status === 201) {
                                          // upload_status=1に更新
                                          await api.put(
                                            ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/updateFileUploadStatus/${fileId}`,
                                            { upload_status: 1 }
                                          );
                                          // fileMetasから削除
                                          setFileMetas(prev => {
                                            const newMetas = { ...prev };
                                            delete newMetas[meta.file.name];
                                            return newMetas;
                                          });
                                          resolve();
                                        } else {
                                          alert('アップロード失敗: ' + meta.file.name);
                                          reject();
                                        }
                                      };
                                      xhr.onerror = () => {
                                        alert('アップロード中にエラー: ' + meta.file.name);
                                        reject();
                                      };
                                      xhr.send(meta.file);
                                      setUploadProgress(prev => ({ ...prev, [meta.file.name]: 0 }));
                                    });
                                  }
                                  // 追加: 基本情報等を保存
                                  try {
                                    const res = await api.put(
                                      ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/info/${model_id}`,
                                      baseInfo
                                    );
                                    const data = await res.json();
                                    if (!data.result) {
                                      alert('基本情報の保存に失敗しました: ' + (data.errors ? data.errors.join(',') : ''));
                                    }
                                  } catch (e) {
                                    alert('基本情報の保存時にエラーが発生しました');
                                  }
                                  // 下書き保存: バージョン情報をPUTで保存
                                  const version = versions.find(v => v.id === selectedVersion);
                                  if (!version) {
                                    alert('バージョンが選択されていません');
                                    return;
                                  }
                                  // 必要なフィールドをまとめて送信
                                  const payload = {
                                    name: version.name,
                                    description: version.description || '',
                                    model_use_type: version.specs.modelUseType,
                                    base_model: version.specs.baseModel,
                                    epochs: version.specs.epochs,
                                    steps: version.specs.steps,
                                    recommended_cfg_scale: version.recommended_settings.cfgScale,
                                    recommended_clip_skip: version.recommended_settings.clipSkip,
                                    recommended_negative_prompt: version.recommended_settings.negativePrompt,
                                    recommended_sampler: version.recommended_settings.sampler,
                                    recommended_steps: version.recommended_settings.steps,
                                    vae: version.recommended_settings.vae,
                                    downloadable: version.specs.downloadable,
                                    display_flg: 0, // 下書き
                                    upload_status: 1, // アップロード済み
                                    style: version.specs.style || ''
                                  };
                                  try {
                                    const res = await api.put(ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/updateVersion/${version.id}`, payload);
                                    const data = await res.json();
                                    if(data.result) {
                                      // バージョンリストを再取得
                                      const infoRes = await api.get(ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/info/${model_id}/edit`);
                                      const infoData = await infoRes.json();
                                      setVersions(infoData.data.versions);
                                    } else {
                                      alert('保存に失敗しました: ' + (data.error || '不明なエラー'));
                                    }
                                  } catch (e) {
                                    alert('保存時にエラーが発生しました: ' + e.message);
                                  }
                                } finally {
                                  await api.put(
                                    ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/updateUploadStatus/${selectedVersion}`,
                                    { upload_status: 1 }
                                  );
                                  // アップロード完了後にモデル基本情報を再取得
                                  try {
                                    const infoRes = await api.get(ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/info/${model_id}/edit`);
                                    const infoData = await infoRes.json();
                                    if (infoData.result && infoData.data) {
                                      setBaseInfo(infoData.data.base_info);
                                      setVersions(infoData.data.versions);
                                    }
                                  } catch (e) {
                                    // 取得失敗時は何もしない
                                  }
                                  // すべてのファイルアップロードが終了した直後にファイル一覧を再取得
                                  const res2 = await api.get(ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/getFiles/${selectedVersion}`);
                                  const data2 = await res2.json();
                                  if (data2.result) {
                                    setModelFileList(prev => ({ ...prev, [selectedVersion]: data2.files }));
                                  }
                                  setUploading(false);
                                }
                                }}
                              >
                                {uploading ? 'アップロード中...' : 'アップロード開始'}
                              </Button>
                              
                              <Typography sx={{ color: 'orangered', mt: 1 }}>
                                モデルはまだアップロードされていません。アップロード後に公開できます。
                              </Typography>
                              </Box>
                            )}

                            {/* サンプル画像のアップロード */}
                        <Box>
                          <Typography variant="subtitle1" gutterBottom>
                            サンプル画像
                          </Typography>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 3,
                              backgroundColor: 'grey.50',
                              textAlign: 'center',
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: 'grey.100'
                              }
                            }}
                            onClick={() => {
                              // ファイル選択ダイアログを開く
                              document.getElementById(`sample-images-upload-${selectedVersion}`).click();
                            }}
                          >
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              style={{ display: 'none' }}
                              id={`sample-images-upload-${selectedVersion}`}
                              onChange={(e) => handleSampleImagesUpload(selectedVersion, e)}
                            />
                            <label>
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 , cursor: 'pointer'}}>
                                <Upload />
                                <Typography>
                                  サンプル画像をドロップ、または選択してください
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  最大20枚まで / 1枚あたり10MB以下
                                </Typography>
                              </Box>
                            </label>
                          </Paper>
                          {/* プレビューエリア */}
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                              gap: 2,
                              mt: 2,
                              justifyContent: 'flex-start', // 追加: 左寄せ
                            }}
                          >
                            {sampleImages[selectedVersion] &&
                              Object.entries(sampleImages[selectedVersion]).map(([key, image], index) => (
                                <Box
                                  key={key}
                                  sx={{
                                    position: 'relative',
                                    width: '100%',
                                    minWidth: 140,
                                    maxWidth: 180,
                                    height: 180,
                                    bgcolor: 'background.paper',
                                    borderRadius: 2,
                                    boxShadow: 3,
                                    overflow: 'hidden',
                                    border: '1px solid',
                                    borderColor: 'grey.200',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                  }}
                                >
                                  {/* 削除ボタン（DB画像はAPIで削除、ローカルは即消す） */}
                                  <IconButton
                                    size="small"
                                    sx={{ position: 'absolute', top: 6, right: 6, backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 2, '&:hover': { backgroundColor: 'rgba(255,255,255,1)' }, boxShadow: 1 }}
                                    onClick={async () => {
                                      if (image.deleting) return;
                                      if (image.id) {
                                        // DB画像
                                        if (!window.confirm('この画像を削除しますか？')) return;
                                        setSampleImages(prev => ({
                                          ...prev,
                                          [selectedVersion]: {
                                            ...prev[selectedVersion],
                                            [key]: { ...image, deleting: true }
                                          }
                                        }));
                                        const res = await api.delete(ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/deleteSampleImage/${image.id}`);
                                        const data = await res.json();
                                        if (data.result) fetchSampleImages(selectedVersion);
                                        else setSampleImages(prev => {
                                          const updated = { ...prev[selectedVersion] };
                                          updated[key] = { ...updated[key], deleting: false };
                                          return { ...prev, [selectedVersion]: updated };
                                        });
                                      } else {
                                        // ローカル
                                        const newImages = { ...sampleImages[selectedVersion] };
                                        delete newImages[key];
                                        setSampleImages({ ...sampleImages, [selectedVersion]: newImages });
                                      }
                                    }}
                                    disabled={!!image.deleting}
                                  >
                                    {image.deleting ? <CircularProgress size={20} /> : <Close fontSize="small" />}
                                  </IconButton>
                                  <Box
                                    sx={{ width: '100%', height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100', borderBottom: '1px solid', borderColor: 'grey.200', cursor: image.status === 'uploading' ? 'default' : 'pointer' }}
                                    onClick={() => image.status !== 'uploading' && setPreviewImage(image)}
                                  >
                                    {image.status === 'pending' ? (
                                      <Box sx={{ width: '100%', textAlign: 'center' }}>
                                        <CircularProgress size={24} color="inherit" sx={{ mt: 2 }} />
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>アップロード待機中...</Typography>
                                      </Box>
                                    ) : image.status === 'uploading' ? (
                                      <Box sx={{ width: '100%', textAlign: 'center' }}>
                                        <CircularProgress size={32} sx={{ mt: 2 }} />
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>アップロード中...</Typography>
                                      </Box>
                                    ) : (
                                      <img
                                        src={image.thumbnail_signed_url || image.thumbnail_url || image.preview_signed_url || image.preview_url || image.url}
                                        alt={image.file?.name || image.file_name || `サンプル${index + 1}`}
                                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover', borderRadius: '4px', display: 'block' }}
                                      />
                                    )}
                                  </Box>
                                  <Box sx={{ p: 1, width: '100%' }}>
                                    <Typography variant="body2" noWrap title={image.file?.name || image.file_name} sx={{ fontWeight: 500 }}>
                                      {image.file?.name || image.file_name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {image.file || image.file_size ? humanFileSize(image.file?.size || image.file_size) : ''}
                                      {image.is_animated ? <Chip size="small" color="info" label="アニメ" sx={{ ml: 1 }} /> : ''}
                                      {image.status === 'pending' && <LinearProgress variant="determinate" value={0} sx={{ mt: 1 }} />}
                                      {image.status === 'uploading' && <LinearProgress variant="determinate" value={image.progress || 0} sx={{ mt: 1 }} />}
                                      {image.status === 'error' && <Typography color="error">{image.error}</Typography>}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))
                            }
                          </Box>
                        </Box>

                      </Box>
                    </CardContent>
                  </Card>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                    <Button variant="outlined" onClick={async () => {
                      // 下書き保存: バージョン情報をPUTで保存
                      const version = versions.find(v => v.id === selectedVersion);
                      if (!version) {
                        alert('バージョンが選択されていません');
                        return;
                      }
                      // 必要なフィールドをまとめて送信
                      const payload = {
                        name: version.name,
                        description: version.description || '',
                        model_use_type: version.specs.modelUseType,
                        base_model: version.specs.baseModel,
                        epochs: version.specs.epochs,
                        steps: version.specs.steps,
                        recommended_cfg_scale: version.recommended_settings.cfgScale,
                        recommended_clip_skip: version.recommended_settings.clipSkip,
                        recommended_negative_prompt: version.recommended_settings.negativePrompt,
                        recommended_sampler: version.recommended_settings.sampler,
                        recommended_steps: version.recommended_settings.steps,
                        vae: version.recommended_settings.vae,
                        downloadable: version.specs.downloadable,
                        display_flg: 0, // 下書き
                        upload_status: version.specs.upload_status || 0,
                        style: version.specs.style || ''
                      };
                      try {
                        const res = await api.put(ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/updateVersion/${version.id}`, payload);
                        const data = await res.json();
                        if(data.result) {
                          alert('下書きとして保存しました');
                          // バージョンリストを再取得
                          const infoRes = await api.get(ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/info/${model_id}/edit`);
                          const infoData = await infoRes.json();
                          setVersions(infoData.data.versions);
                        } else {
                          alert('保存に失敗しました: ' + (data.error || '不明なエラー'));
                        }
                      } catch (e) {
                        alert('保存時にエラーが発生しました: ' + e.message);
                      }
                    }}>下書き保存</Button>
                    {/* ステータス表示 */}
                    {(() => {
                      const version = versions.find(v => v.id === selectedVersion);
                      if (!version) return null;
                      let label = '';
                      let color = 'default';
                      // upload_status優先で表示（数値型も考慮）
                      if (version.specs && (version.specs.upload_status === 0 || version.specs.upload_status === '0')) {
                        label = '下書き'; color = 'warning';
                      } else if (version.status === 'published') {
                        label = '公開中'; color = 'success';
                      } else if (version.status === 'waiting') {
                        label = '公開待ち'; color = 'secondary';
                      } else if (version.status === 'draft') {
                        label = '下書き'; color = 'warning';
                      } else {
                        label = version.status || '不明'; color = 'default';
                      }
                      return (
                        <Chip label={label} color={color} size="small" />
                      );
                    })()}
                  </Box>
                  <Button variant="contained" onClick={async () => {
                    // まず下書き保存
                    const version = versions.find(v => v.id === selectedVersion);
                    if (!version) {
                      alert('バージョンが選択されていません');
                      return;
                    }
                    const payload = {
                      name: version.name,
                      description: version.description || '',
                      model_use_type: version.specs.modelUseType,
                      base_model: version.specs.baseModel,
                      epochs: version.specs.epochs,
                      steps: version.specs.steps,
                      recommended_cfg_scale: version.recommended_settings.cfgScale,
                      recommended_clip_skip: version.recommended_settings.clipSkip,
                      recommended_negative_prompt: version.recommended_settings.negativePrompt,
                      recommended_sampler: version.recommended_settings.sampler,
                      recommended_steps: version.recommended_settings.steps,
                      vae: version.recommended_settings.vae,
                      downloadable: version.specs.downloadable,
                      display_flg: 0, // 下書き
                      upload_status: version.specs.upload_status || 0,
                      style: version.specs.style || ''
                    };
                    try {
                      const res = await api.put(ApiFetch.API_PROTOCOL + '//' + ApiFetch.API_HOST + `/v1/model/updateVersion/${version.id}`, payload);
                      const data = await res.json();
                      if (!data.result) {
                        alert('下書き保存に失敗しました: ' + (data.error || '不明なエラー'));
                        return;
                      }
                    } catch (e) {
                      alert('下書き保存時にエラーが発生しました: ' + e.message);
                      return;
                    }
                    // 下書き保存成功時のみ公開処理
                    handlePublishVersion();
                  }} disabled={(() => {
                    const v = versions.find(v => v.id === selectedVersion);
                    return !v || v.specs.upload_status !== 1 && v.specs.upload_status !== '1';
                  })()}>
                    このバージョンを公開
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Typography variant="h6">利用許諾範囲</Typography>

                <Box>
                  <Typography variant="h6">このモデルを使用する際、ユーザーに以下の許可を与えます</Typography>
                  <Box>
                    <FormControlLabel
                      disabled
                      control={<Checkbox />}
                      label="アワートAIの生成機能で使用(必須)"
                      checked={baseInfo.usage_permission_generate}
                      onChange={(e) => setBaseInfo({ ...baseInfo, usage_permission_generate: e.target.checked })}
                    />
                  </Box>
                  <Box>
                    <FormControlLabel
                      disabled
                      control={<Checkbox />}
                      label="アワートAIでのオンライントレーニングベースモデルとして使用(必須)"
                      checked={baseInfo.usage_permission_online_training}
                      onChange={(e) => setBaseInfo({ ...baseInfo, usage_permission_online_training: e.target.checked })}
                    />
                  </Box>
                  <Box>
                    <FormControlLabel
                      control={<Checkbox />}
                      label="クレジットなしで使用する"
                      checked={baseInfo.usage_permission_no_credit}
                      onChange={(e) => setBaseInfo({ ...baseInfo, usage_permission_no_credit: e.target.checked })}
                    />
                  </Box>
                  <Box>
                    <FormControlLabel
                      control={<Checkbox />}
                      label="モデルの派生利用を許可する"
                      checked={baseInfo.usage_permission_derivative}
                      onChange={(e) => setBaseInfo({ ...baseInfo, usage_permission_derivative: e.target.checked })}
                    />
                  </Box>
                  <Box>
                    <FormControlLabel
                      control={<Checkbox />}
                      label="モデルの派生後、別のライセンスを適用可能"
                      checked={baseInfo.usage_permission_derivative_license}
                      onChange={(e) => setBaseInfo({ ...baseInfo, usage_permission_derivative_license: e.target.checked })}
                    />
                  </Box>
                  <Box>
                    <FormControlLabel
                      control={<Checkbox />}
                      label="モデルを再配布する"
                      checked={baseInfo.usage_permission_redistribute}
                      onChange={(e) => setBaseInfo({ ...baseInfo, usage_permission_redistribute: e.target.checked })}
                    />
                  </Box>
                  <Typography variant="h6" className='mt-3'>商用ライセンス</Typography>
                  <Box>
                    <FormControlLabel
                      control={<Checkbox />}
                      label={
                        <Box>
                          <Typography variant="subtitle1">生成画像への権利不行使(販売可能)</Typography>
                          <Typography variant="body2" color="text.secondary">
                            このモデルで生成された画像に対して、モデル製作者として権利を主張しません
                          </Typography>
                        </Box>
                      }
                      checked={baseInfo.commercial_license_no_rights}
                      onChange={(e) => setBaseInfo({ ...baseInfo, commercial_license_no_rights: e.target.checked })}
                    />
                  </Box>
                  <Box>
                    <FormControlLabel
                      control={<Checkbox />}
                      label="画像生成サービスとしての商用利用を許可する"
                      checked={baseInfo.commercial_license_service}
                      onChange={(e) => setBaseInfo({ ...baseInfo, commercial_license_service: e.target.checked })}
                    />
                  </Box>
                  <Box>
                    <FormControlLabel
                      control={<Checkbox />}
                      label="販売またはモデルの派生による商用利用を許可する"
                      checked={baseInfo.commercial_license_derivative}
                      onChange={(e) => setBaseInfo({ ...baseInfo, commercial_license_derivative: e.target.checked })}
                    />
                  </Box>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mt: 2 }}>ライセンス</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    ライセンス名称とURLを入力してください。名称は候補から選択または自由入力できます。
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        freeSolo
                        options={licenseCandidates.map(c => c.name)}
                        value={baseInfo.license || ''}
                        onInputChange={(event, newInputValue) => {
                          setBaseInfo({ ...baseInfo, license: newInputValue });
                          // 候補選択時はURLも自動でセット
                          const found = licenseCandidates.find(c => c.name === newInputValue);
                          if (found) {
                            setBaseInfo(prev => ({ ...prev, license_url: found.url, license: found.name }));
                            setCustomLicenseUrl(found.url);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="ライセンス名称"
                            placeholder="例: Creative Commons BY 4.0"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="ライセンスURL"
                        value={baseInfo.license_url || ''}
                        onChange={e => {
                          setCustomLicenseUrl(e.target.value);
                          setBaseInfo({ ...baseInfo, license_url: e.target.value });
                        }}
                        placeholder="例: https://creativecommons.org/licenses/by/4.0/deed.ja"
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={{ mt: 3, textAlign: 'right' }}>
                  <Button variant="contained" color="primary" onClick={handleSaveLicenseAndPermissions}>
                    利用許諾範囲とライセンスを保存する
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>
      </Box>

      {/* 画像プレビューダイアログ */}
      <Dialog open={!!previewImage} onClose={() => setPreviewImage(null)} maxWidth="md" fullWidth>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
          {previewImage && (
            <img
              src={previewImage.preview_signed_url || previewImage.preview || previewImage.url}
              alt={previewImage.file?.name || previewImage.file_name}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                borderRadius: 8,
                boxShadow: '0 2px 16px rgba(0,0,0,0.2)'
              }}
            />
          )}
          {previewImage && (
            <Typography variant="body1" sx={{ mt: 2, wordBreak: 'break-all' }}>{previewImage.file?.name || previewImage.file_name}</Typography>
          )}
          {previewImage && (
            <Typography variant="caption" color="text.secondary">{humanFileSize(previewImage.file?.size || previewImage.file_size)}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewImage(null)} color="primary">閉じる</Button>
        </DialogActions>
      </Dialog>
    </Container>
    <Footer />
    </div>
  );
};

export default ModelUploadForm;

// 編集用取得
export async function fetchModelInfoEdit(modelId) {
  return await new ApiFetch().get(`/v1/model/info/${modelId}?forEdit=1`).then(r => r.json());
}
// 公開用取得
export async function fetchModelInfoPublic(modelId) {
  return await new ApiFetch().get(`/v1/model/info/${modelId}`).then(r => r.json());
}